import React, { useState } from 'react';
import { User } from '../types/User';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { AuthService } from '../services/authService';

interface RegisterPageProps {
  onRegister: (user: User) => void;
  onBackToLogin: () => void;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ onRegister, onBackToLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // First test the connection
      console.log('Testing Supabase connection...');
      const { success, error: connectionError } = await AuthService.testConnection();
      
      if (!success) {
        setErrors({ 
          email: `Connection failed: ${connectionError}. Please check your internet connection and try again.` 
        });
        setIsLoading(false);
        return;
      }

      console.log('Connection test passed, proceeding with registration...');

      // Register with Supabase
      const { user, error } = await AuthService.register({
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name,
        company: formData.company,
        position: '',
        country: '',
        about: ''
      });

      if (error) {
        console.error('Registration error:', error);
        if (error.includes('already registered')) {
          setErrors({ email: 'This email is already registered. Please use a different email or try logging in.' });
        } else if (error.includes('Network error')) {
          setErrors({ email: error });
        } else {
          setErrors({ email: error });
        }
      } else if (user) {
        console.log('Registration successful:', user);
        onRegister(user);
      }
    } catch (error) {
      console.error('Unexpected error during registration:', error);
      setErrors({ 
        email: 'An unexpected error occurred. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url('https://events.logisticsmarket.rs/wp-content/uploads/2025/09/LM-layout.png')`
      }}
    >
      <div className="absolute inset-0 bg-white bg-opacity-60"></div>
      <div className="absolute inset-0" style={{ backgroundColor: 'rgb(0 0 0 / 40%)' }}></div>
      
      <div className="relative z-10 bg-white rounded-lg shadow-2xl p-8 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
        <div className="text-center mb-6">
          <img 
            src="https://events.logisticsmarket.rs/wp-content/uploads/2022/02/Logistics-Market-logo-6.webp"
            alt="Logistics Market Logo"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Create Account</h1>
          <p className="text-sm text-gray-600">LOGISTICS MARKET EXPO & CONFERENCE 2026</p>
          <p className="text-xs text-gray-500 mt-1">6 October 2026 · Beogradski sajam, Hala 5</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                errors.name ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-yellow-600'
              }`}
              placeholder="Your full name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                errors.email ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-yellow-600'
              }`}
              placeholder="your.email@company.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
              Company *
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                errors.company ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-yellow-600'
              }`}
              placeholder="Your company name"
            />
            {errors.company && <p className="mt-1 text-sm text-red-600">{errors.company}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                  errors.password ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-yellow-600'
                }`}
                placeholder="••••••••••"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 ${
                  errors.confirmPassword ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-yellow-600'
                }`}
                placeholder="••••••••••"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full text-white py-3 px-4 rounded-md font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition duration-200 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            style={{ backgroundColor: '#C89116' }}
          >
            {isLoading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="flex items-center justify-center space-x-2 text-sm hover:opacity-80 underline mx-auto"
              style={{ color: '#C89116' }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Login</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;